import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getClientWiseAudit } from "../../../services/audit_redux/audit_actions/Audit.action";
import { getClients } from "../../../services/global_redux/globale_actions/Client.action";
import { HiCheck } from "react-icons/hi";
import "./TrackAudit.css";

const STEPS = [
  { status: "Audit", title: "Created", text: "Sent to the vendor" },
  { status: "Vendor-In-Progress", title: "Vendor working", text: "Vendor is filling the audit" },
  { status: "Audit-Pending", title: "Submitted", text: "Vendor submitted the audit" },
  { status: "Auditor-In-progress", title: "Under review", text: "Auditor is reviewing" },
  { status: "Completed", title: "Completed", text: "Audit is finished" },
];

function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function getAssignedClientUnits() {
  try {
    const mapped = JSON.parse(getSessionUser().client_and_unit || "[]");
    return Array.isArray(mapped) ? mapped : [];
  } catch {
    return [];
  }
}

function getLockedClientId() {
  const stored = sessionStorage.getItem("selectedClient");
  if (stored && stored !== "null") return stored;
  return getAssignedClientUnits()[0]?.client_id || "";
}

function getAssignedUnits(clientId) {
  const entry = getAssignedClientUnits().find(
    (item) => String(item.client_id) === String(clientId)
  );
  return (entry?.unit_id || []).map(String);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function branchLabel(row) {
  const name = String(row?.unit_name || "").trim();
  const city = String(row?.Location_City || "").trim();
  const state = String(row?.state || "").trim();
  const location = state && city ? `${state}, ${city}` : (state || city);
  if (name && location) return `${name} (${location})`;
  return name || location || "-";
}

function toYearMonth(label) {
  const raw = String(label || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const named = raw.match(/^([A-Za-z]{3})-(\d{4})$/);
  if (!named) return "";
  const index = MONTHS.findIndex((month) => month.toLowerCase() === named[1].toLowerCase());
  if (index < 0) return "";
  return `${named[2]}-${String(index + 1).padStart(2, "0")}`;
}

function monthInRange(row, month) {
  if (!month) return true;
  const start = toYearMonth(row.start_period);
  const end = toYearMonth(row.end_period);
  if (!start || !end) return false;
  return start <= month && month <= end;
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function TrackAudit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewId = searchParams.get("id");
  const isClient = getSessionUser()?.base_role === "Client";
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [branchId, setBranchId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [month, setMonth] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isClient) {
      navigate("/dashboard");
      return;
    }
    const clientId = getLockedClientId();
    if (!clientId) {
      setRows([]);
      setLoaded(true);
      return;
    }
    let active = true;
    (async () => {
      const [auditResponse, clientResponse] = await Promise.all([
        dispatch(getClientWiseAudit(clientId)),
        dispatch(getClients()),
      ]);
      if (!active) return;
      const clients = Array.isArray(clientResponse?.data) ? clientResponse.data : [];
      const matched = clients.find((client) => String(client.client_id) === String(clientId));
      if (!auditResponse?.success) {
        setRows([]);
        setOrgName(matched?.client_name || "");
        setLoaded(true);
        return;
      }
      const assigned = getAssignedUnits(clientId);
      const list = Array.isArray(auditResponse.data?.data) ? auditResponse.data.data : [];
      const visible = list.filter(
        (item) => !assigned.length || assigned.includes(String(item.unit_id))
      );
      setRows(visible);
      setOrgName(matched?.client_name || visible[0]?.client_name || "");
      setLoaded(true);
    })();
    return () => {
      active = false;
    };
  }, [dispatch, isClient, navigate]);

  useEffect(() => {
    if (!loaded) return;
    if (!viewId) {
      setSelected(null);
      return;
    }
    const match = rows.find((row) => String(row.id) === String(viewId));
    if (!match) {
      setSearchParams({}, { replace: true });
      setSelected(null);
      return;
    }
    setSelected(match);
  }, [loaded, rows, viewId, setSearchParams]);

  const branches = useMemo(() => {
    const seen = new Map();
    rows.forEach((row) => {
      const id = String(row.unit_id || "");
      if (!id || seen.has(id)) return;
      seen.set(id, branchLabel(row));
    });
    return Array.from(seen, ([id, label]) => ({ id, label }));
  }, [rows]);

  const vendors = useMemo(() => {
    const source = branchId ? rows.filter((row) => String(row.unit_id) === String(branchId)) : rows;
    const seen = new Map();
    source.forEach((row) => {
      const id = String(row.vendor_id || row.vendor_name || "");
      if (!id || seen.has(id)) return;
      seen.set(id, row.vendor_name || id);
    });
    return Array.from(seen, ([id, label]) => ({ id, label }));
  }, [rows, branchId]);

  const monthBounds = useMemo(() => {
    const today = currentYearMonth();
    const starts = rows.map((row) => toYearMonth(row.start_period)).filter(Boolean).sort();
    const ends = rows.map((row) => toYearMonth(row.end_period)).filter(Boolean).sort();
    const earliest = starts[0] || "";
    const latest = ends[ends.length - 1] || "";
    const maxMonth = !latest || latest > today ? today : latest;
    const minMonth = !earliest || earliest > maxMonth ? maxMonth : earliest;
    return { minMonth, maxMonth };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (branchId && String(row.unit_id) !== String(branchId)) return false;
      if (vendorId && String(row.vendor_id || row.vendor_name) !== String(vendorId)) return false;
      if (!monthInRange(row, month)) return false;
      return true;
    });
  }, [rows, branchId, vendorId, month]);

  const onBranchChange = (value) => {
    setBranchId(value);
    setVendorId("");
  };

  const onMonthChange = (value) => {
    if (!value) {
      setMonth("");
      return;
    }
    if (value < monthBounds.minMonth || value > monthBounds.maxMonth) return;
    setMonth(value);
  };

  const openStatus = (row) => {
    setSearchParams({ id: String(row.id) });
  };

  const closeStatus = () => {
    setSearchParams({});
  };

  const currentIndex = STEPS.findIndex((step) => step.status === selected?.status);
  const expired = selected?.status === "Validity-expired";

  return (
    <div className="track-audit-page">
      <h5 className="text-white title">Track Audit</h5>
      <div className="track-audit-card">
        {viewId && !selected ? (
          <p className="track-audit-empty">Loading audit...</p>
        ) : selected ? (
          <>
            <div className="track-status-head">
              <div className="track-audit-title">
                <p className="track-info-chip"><span>Audit Name :</span> <strong>{selected.audit_name}</strong></p>
                <p className={`track-info-chip${expired ? " is-expired" : ""}`}>
                  <span>Status :</span>{" "}
                  <strong className="track-status-word">{expired ? "Validity expired" : (STEPS[currentIndex]?.title || "In progress")}</strong>
                </p>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeStatus}>
                Back
              </button>
            </div>
            <div className="track-detail-grid">
              <div>
                <span>Organisation</span>
                <strong>{orgName || selected.client_name || "-"}</strong>
              </div>
              <div>
                <span>Branch</span>
                <strong>{branchLabel(selected)}</strong>
              </div>
              <div>
                <span>Period</span>
                <strong>{selected.start_period} to {selected.end_period}</strong>
              </div>
              <div>
                <span>Vendor</span>
                <strong>{selected.vendor_name || "-"}</strong>
              </div>
            </div>
            {expired ? (
              <div className="track-expired">Validity expired. This audit was not completed in time.</div>
            ) : (
              <div className="track-journey">
                {STEPS.map((step, index) => {
                  const finished = index < currentIndex || (step.status === "Completed" && index === currentIndex);
                  const state = finished ? "done" : index === currentIndex ? "current" : "";
                  return (
                    <div key={step.status} className={`track-journey-step ${state}`}>
                      <div className="track-journey-rail">
                        {finished ? (
                          <HiCheck className="track-journey-tick" aria-hidden="true" />
                        ) : (
                          <span className="track-journey-dot" />
                        )}
                      </div>
                      <div>
                        <p>{step.title}</p>
                        <span>{step.text}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
          <div className="track-filters">
            <div>
              <label>Organisation Name</label>
              <input className="form-control form-control-sm track-org-locked" value={orgName} readOnly />
            </div>
            <div>
              <label>Branch</label>
              <select className="form-select form-select-sm" value={branchId} onChange={(e) => onBranchChange(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Month</label>
              <input
                type="month"
                className="form-control form-control-sm"
                min={monthBounds.minMonth}
                max={monthBounds.maxMonth}
                value={month}
                onChange={(e) => onMonthChange(e.target.value)}
              />
            </div>
            <div>
              <label>Vendor</label>
              <select className="form-select form-select-sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">All vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>{vendor.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="table-responsive track-audit-scroll">
            <table className="table table-bordered mb-0">
              <thead>
                <tr>
                  <th>SL No.</th>
                  <th>Audit Name</th>
                  <th>Start Period</th>
                  <th>End Period</th>
                  <th>Vendor Name</th>
                  <th>Branch</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="track-audit-empty">No audits to track.</td>
                  </tr>
                ) : (
                  filteredRows.map((row, index) => (
                    <tr key={row.id || `${row.audit_id}-${row.unit_id}-${row.vendor_id}`}>
                      <td>{index + 1}</td>
                      <td>{row.audit_name}</td>
                      <td>{row.start_period}</td>
                      <td>{row.end_period}</td>
                      <td>{row.vendor_name}</td>
                      <td>{branchLabel(row)}</td>
                      <td>
                        <button type="button" className="btn btn-success btn-sm" onClick={() => openStatus(row)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrackAudit;
